import { apiFetch, safeJson } from './http';

export type MeResponse = {
  publicId: string;
  firstName: string;
  lastName?: string;
  email: string;
  phone?: string;
  companyName?: string;
  position?: string;
  vat?: string;
  country?: { country: string; iso2: string; iso3: string };
  isEmailConfirmed: boolean;
  createdAt: string;
  role?: string;
};

export type TrialCredentials = {
  internalNumber: number;
  trialDays: number;
  trialStartedAt: string;
  trialEndedAt: string;
  isActive: boolean;
  password: string;
};

export type AdminUserRow = {
  publicId: string;
  firstName: string;
  lastName?: string;
  email: string;
  phone?: string;
  companyName?: string;
  role: string;
  registeredBy: string;
  isEmailConfirmed: boolean;
  createdAt: string;
  country?: { country: string; iso2: string; iso3: string };
  trial: {
    internalNumber: number;
    trialDays: number;
    trialStartedAt: string;
    trialExpiresAt: string | null;
    trialStatus: 'active' | 'expired' | 'none';
    trialUid?: string;
  } | null;
};

export async function listAdminUsers(token: string): Promise<AdminUserRow[]> {
  const res = await apiFetch<{ data: AdminUserRow[] }>('/users/admin/list', { method: 'GET', token });
  return res.data;
}

export async function updateAdminUserRole(token: string, publicId: string): Promise<AdminUserRow> {
  const res = await apiFetch<{ data: AdminUserRow }>(`/users/admin/${publicId}/role`, {
    method: 'PATCH',
    token,
  });
  return res.data;
}

export async function extendAdminUserTrial(token: string, publicId: string): Promise<AdminUserRow> {
  const res = await apiFetch<{ data: AdminUserRow }>(`/users/admin/${publicId}/trial/extend`, {
    method: 'POST',
    token,
  });
  return res.data;
}

export async function toggleAdminUserTrial(token: string, publicId: string): Promise<AdminUserRow> {
  const res = await apiFetch<{ data: AdminUserRow }>(`/users/admin/${publicId}/trial/toggle`, {
    method: 'POST',
    token,
  });
  return res.data;
}

export async function deleteAdminUser(token: string, publicId: string): Promise<{ deleted: boolean; publicId: string }> {
  const res = await apiFetch<{ data: { deleted: boolean; publicId: string } }>(`/users/admin/${publicId}`, {
    method: 'DELETE',
    token,
  });
  return res.data;
}

export async function getMe(token: string): Promise<MeResponse> {
  return apiFetch<MeResponse>('/users/me', { method: 'GET', token });
}

export async function updateMe(
  token: string,
  data: Partial<{
    firstName: string;
    lastName: string;
    phone: string;
    companyName: string;
    countryId: number;
    position: string;
    vat: string;
  }>
): Promise<MeResponse> {
  return apiFetch<MeResponse>('/users/me/update', { method: 'PATCH', token, body: safeJson(data) });
}

export async function createTrialAccount(token: string, password: string): Promise<TrialCredentials> {
  return apiFetch<TrialCredentials>('/users/me/trial/create-account', {
    method: 'POST',
    token,
    body: safeJson({ password }),
  });
}

export async function getTrialCredentials(token: string): Promise<TrialCredentials> {
  return apiFetch<TrialCredentials>('/users/me/trial/credentials', { method: 'GET', token });
}

export async function loginTrial(token: string): Promise<{ token: string }> {
  return apiFetch<{ token: string }>('/users/me/trial/login', { method: 'POST', token });
}

export async function extendTrial(token: string): Promise<TrialCredentials> {
  return apiFetch<TrialCredentials>('/users/me/trial/extend', { method: 'POST', token });
}

export type CompanyInformation = {
  companyName: string | null;
  companyEmail: string | null;
  registrationNumber: string | null;
  companyAddress: string | null;
  postalCode: string | null;
  timezone: string | null;
  countryId: number | null;
  country: { id: number; country: string; iso2: string; iso3: string } | null;
  industry: string | null;
  employeeCount: string | null;
  additionalInfo: string | null;
  contactFullName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  contactPosition: string | null;
  lockedFields: string[];
  requiredFields: string[];
};

export type CompanyInformationPayload = {
  companyName?: string | null;
  companyEmail?: string | null;
  registrationNumber?: string | null;
  companyAddress?: string | null;
  postalCode?: string | null;
  timezone?: string | null;
  countryId?: number | null;
  industry?: string | null;
  employeeCount?: string | null;
  additionalInfo?: string | null;
  contactFullName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  contactPosition?: string | null;
};

export async function getCompanyInformation(token: string): Promise<CompanyInformation> {
  const res = await apiFetch<{ data: CompanyInformation }>('/users/me/company-information', {
    method: 'GET',
    token,
  });
  return res.data;
}

export async function upsertCompanyInformation(
  token: string,
  data: CompanyInformationPayload
): Promise<CompanyInformation> {
  const res = await apiFetch<{ data: CompanyInformation }>('/users/me/company-information', {
    method: 'PUT',
    token,
    body: safeJson(data),
  });
  return res.data;
}

